import React from "react";
import { Box, styled, Tooltip } from "@mui/material";
import type { HatSentiment } from "../../types";

interface SentimentButtonsProps {
  sentiment?: HatSentiment;
  onChange?: (sentiment: HatSentiment) => void;
}

const Container = styled(Box, {
  label: "SentimentButtonsContainer",
})(() => ({
  display: "flex",
  gap: 24,
  alignItems: "center",
}));

const IconButton = styled(Box, {
  label: "SentimentIconButton",
})(() => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "opacity 0.2s ease, transform 0.2s ease",
  "&:hover": {
    opacity: 0.8,
    transform: "scale(1.1)",
  },
}));

const CIRCLE_PATH =
  "M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z";

const HeartIcon = ({ active }: { active?: boolean }) => {
  const bg = active ? "#FFF1EE" : "#F4F6F8";
  const fill = active ? "#EA1D76" : "#F4F6F8";
  const stroke = active ? "#EA1D76" : "#A5B0BA";
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={CIRCLE_PATH} fill={bg} />
      <path
        d="M10 17.2465C10 22.1095 14.02 24.7005 16.962 27.0205C18 27.8385 19 28.6095 20 28.6095C21 28.6095 22 27.8395 23.038 27.0195C25.981 24.7015 30 22.1095 30 17.2475C30 12.3845 24.5 8.93546 20 13.6115C15.5 8.93446 10 12.3835 10 17.2465Z"
        fill={fill}
        stroke={stroke}
        strokeWidth={active ? 1 : 2}
      />
    </svg>
  );
};

const ThumbUpIcon = ({ active }: { active?: boolean }) => {
  const bg = active ? "#EBF6FE" : "#F4F6F8";
  const fill = active ? "#078DEE" : "#F4F6F8";
  const stroke = active ? "#078DEE" : "#A5B0BA";
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 41 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={CIRCLE_PATH} fill={bg} />
      <path
        d="M28.27 24.2655L28.975 20.1855C29.0163 19.9464 29.005 19.7011 28.9416 19.4668C28.8783 19.2325 28.7646 19.0149 28.6084 18.8292C28.4522 18.6434 28.2573 18.4941 28.0373 18.3915C27.8173 18.289 27.5776 18.2357 27.335 18.2355H22.154C22.033 18.2355 21.9136 18.2092 21.8038 18.1584C21.6941 18.1076 21.5968 18.0336 21.5185 17.9414C21.4403 17.8492 21.3831 17.741 21.3509 17.6245C21.3187 17.5079 21.3122 17.3858 21.332 17.2665L21.995 13.2215C22.1025 12.5647 22.0719 11.8927 21.905 11.2485C21.8335 10.9823 21.696 10.7385 21.505 10.5397C21.3141 10.3409 21.0761 10.1936 20.813 10.1115L20.668 10.0645C20.3401 9.95919 19.9844 9.98352 19.674 10.1325C19.334 10.2965 19.086 10.5955 18.994 10.9505L18.518 12.7845C18.3664 13.3681 18.1462 13.9317 17.862 14.4635C17.447 15.2405 16.805 15.8635 16.137 16.4385L14.698 17.6785C14.4983 17.851 14.3424 18.0684 14.243 18.3128C14.1436 18.5571 14.1035 18.8216 14.126 19.0845L14.938 28.4775C14.9737 28.8929 15.164 29.2798 15.4711 29.5618C15.7783 29.8437 16.18 30.0003 16.597 30.0005H21.245C24.727 30.0005 27.698 27.5745 28.27 24.2655Z"
        fill={fill}
        stroke={stroke}
        strokeWidth={2}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.968 17.4855C11.1612 17.477 11.3503 17.5436 11.4956 17.6712C11.641 17.7989 11.7314 17.9778 11.748 18.1705L12.718 29.4065C12.7344 29.5738 12.7165 29.7427 12.6655 29.9029C12.6145 30.0631 12.5313 30.2112 12.4211 30.3382C12.3109 30.4652 12.176 30.5683 12.0246 30.6414C11.8731 30.7145 11.7084 30.7559 11.5405 30.7632C11.3725 30.7705 11.2048 30.7435 11.0476 30.6838C10.8905 30.6241 10.7471 30.533 10.6263 30.416C10.5056 30.299 10.4099 30.1587 10.3452 30.0035C10.2805 29.8483 10.2481 29.6816 10.25 29.5135V18.2345C10.25 18.0412 10.3248 17.8554 10.4585 17.7158C10.5923 17.5763 10.7748 17.4937 10.968 17.4855Z"
        fill={stroke}
      />
    </svg>
  );
};

const ThumbDownIcon = ({ active }: { active?: boolean }) => {
  const bg = active ? "#FFF8EB" : "#F4F6F8";
  const fill = active ? "#FFAB00" : "#F4F6F8";
  const stroke = active ? "#FFAB00" : "#A5B0BA";
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 41 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={CIRCLE_PATH} fill={bg} />
      <path
        d="M28.27 15.7345L28.975 19.8145C29.0163 20.0536 29.005 20.2989 28.9416 20.5332C28.8783 20.7675 28.7646 20.9851 28.6084 21.1708C28.4522 21.3566 28.2573 21.5059 28.0373 21.6085C27.8173 21.711 27.5776 21.7643 27.335 21.7645L22.154 21.7645C22.033 21.7645 21.9136 21.7908 21.8038 21.8416C21.6941 21.8924 21.5968 21.9664 21.5185 22.0586C21.4403 22.1508 21.3831 22.259 21.3509 22.3755C21.3187 22.4921 21.3122 22.6142 21.332 22.7335L21.995 26.7785C22.1025 27.4353 22.0719 28.1073 21.905 28.7515C21.8335 29.0177 21.696 29.2615 21.505 29.4603C21.3141 29.6591 21.0761 29.8064 20.813 29.8885L20.668 29.9355C20.3401 30.0408 19.9844 30.0165 19.674 29.8675C19.334 29.7035 19.086 29.4045 18.994 29.0495L18.518 27.2155C18.3664 26.6319 18.1462 26.0683 17.862 25.5365C17.447 24.7595 16.805 24.1365 16.137 23.5615L14.698 22.3215C14.4983 22.149 14.3424 21.9316 14.243 21.6872C14.1436 21.4429 14.1035 21.1784 14.126 20.9155L14.938 11.5225C14.9737 11.1071 15.164 10.7202 15.4711 10.4382C15.7783 10.1563 16.18 9.99972 16.597 9.9995L21.245 9.9995C24.727 9.9995 27.698 12.4255 28.27 15.7345Z"
        fill={fill}
        stroke={stroke}
        strokeWidth={active ? 1 : 2}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.968 22.5145C11.1612 22.523 11.3503 22.4564 11.4956 22.3288C11.641 22.2011 11.7314 22.0222 11.748 21.8295L12.718 10.5935C12.7344 10.4262 12.7165 10.2573 12.6655 10.0971C12.6145 9.93689 12.5313 9.78878 12.4211 9.66181C12.3109 9.53483 12.176 9.43165 12.0246 9.35858C11.8731 9.2855 11.7084 9.24405 11.5405 9.23677C11.3725 9.22949 11.2048 9.25652 11.0476 9.31622C10.8905 9.37592 10.7471 9.46703 10.6263 9.58399C10.5056 9.70095 10.4099 9.84131 10.3452 9.99648C10.2805 10.1517 10.2481 10.3184 10.25 10.4865L10.25 21.7655C10.25 21.9588 10.3248 22.1446 10.4585 22.2842C10.5923 22.4237 10.7748 22.5063 10.968 22.5145Z"
        fill={stroke}
      />
    </svg>
  );
};

const NotSureIcon = ({ active }: { active?: boolean }) => {
  const bg = active ? "#EBF9FC" : "#F4F6F8";
  const color = active ? "#00B8D9" : "#A5B0BA";
  const idleStrokeWidth = 1.2;
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={CIRCLE_PATH} fill={bg} />
      <g transform="translate(8, 8)">
        <circle
          cx="12.0952"
          cy="18.6562"
          r="2.1"
          fill={active ? color : "none"}
          stroke={active ? "none" : color}
          strokeWidth={active ? 0 : idleStrokeWidth}
          strokeLinecap="round"
        />
        <path
          d="M12.095 1C8.73419 1 6 3.73363 6 7.09375V7.67969H10.6885V7.09375C10.6885 6.31832 11.3194 5.6875 12.095 5.6875C12.8706 5.6875 13.5015 6.31832 13.5015 7.09375C13.5015 7.50695 13.3201 7.89805 13.0038 8.16684L9.75077 10.932V15.1406H14.4392V13.0993L16.0402 11.7385C17.4064 10.5778 18.19 8.88488 18.19 7.09375C18.19 3.73363 15.4558 1 12.095 1Z"
          fill={active ? color : bg}
          stroke={active ? "none" : color}
          strokeWidth={active ? 0 : idleStrokeWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
};

export const SentimentButtons = ({
  sentiment,
  onChange,
}: SentimentButtonsProps) => {
  const handleClick = (value: HatSentiment) => {
    onChange?.(sentiment === value ? null : value);
  };

  return (
    <Container>
      <Tooltip title="Love" placement="bottom" arrow>
        <IconButton
          onClick={() => handleClick("LOVE")}
          aria-label="Love this career"
          role="button"
        >
          <HeartIcon active={sentiment === "LOVE"} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Like" placement="bottom" arrow>
        <IconButton
          onClick={() => handleClick("LIKE")}
          aria-label="Like this career"
          role="button"
        >
          <ThumbUpIcon active={sentiment === "LIKE"} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Not for Me" placement="bottom" arrow>
        <IconButton
          onClick={() => handleClick("NOTFORME")}
          aria-label="Not for me"
          role="button"
        >
          <ThumbDownIcon active={sentiment === "NOTFORME"} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Not Sure Yet" placement="bottom" arrow>
        <IconButton
          onClick={() => handleClick("NOT_SURE")}
          aria-label="Not sure"
          role="button"
        >
          <NotSureIcon active={sentiment === "NOT_SURE"} />
        </IconButton>
      </Tooltip>
    </Container>
  );
};
